/**
 * Client Explorer — the way into Client 360, and therefore into the timeline.
 *
 * The three scores stay separate here exactly as they do on the profile (§27):
 * sorting by Opportunity is how you find the lapsing high-value referrers, and
 * that list is different from the one Health or Risk would give you.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import { Disclosure } from "@/components/cards/Disclosure";
import { KpiCard } from "@/components/cards/KpiCard";
import { DataTable, type Column } from "@/components/tables/DataTable";
import type { AnalyticsEnvelope } from "@/types/api";
import { AlertTriangle } from "lucide-react";
import { count, inr, pct } from "@/utils/format";

interface ClientRow {
  client_id: string;
  name: string;
  referral_type: string;
  area: string;
  designation: string;
  owner: string;
  owner_key: string;
  activities: number;
  activities_90d: number;
  leads: number;
  admissions: number;
  conversion_pct: number | null;
  revenue: number;
  commission: number;
  days_since_activity: number | null;
  days_since_credit: number | null;
  rfm_segment: string;
  lifecycle: string;
  health: number | null;
  opportunity: number | null;
  risk: number | null;
}

interface Summary {
  clients: number;
  revenue: number;
  at_risk: number;
  dormant: number;
}

const SEGMENTS = ["Champion", "Loyal", "High potential", "Promising",
                  "Needs attention", "At risk", "Lost", "Low value"];
const LIFECYCLES = ["Prospect", "New", "Activated", "Engaged", "Producing",
                    "High Value", "Declining", "Dormant", "Reactivated", "Lost"];

/** A 0-100 score rendered as a number plus a bar, so a column scans at a glance. */
function ScoreCell({ value, invert = false }: { value: number | null; invert?: boolean }) {
  if (value === null || value === undefined) {
    return <span className="text-ink-faint">—</span>;
  }
  const good = invert ? value < 40 : value >= 60;
  const mid = invert ? value < 60 : value >= 40;
  const colour = good ? "#2F6B3C" : mid ? "#8A6410" : "#9E3B2E";
  return (
    <span className="inline-flex items-center gap-1.5 justify-end w-full">
      <span className="tnum" style={{ color: colour }}>{value.toFixed(0)}</span>
      <span className="w-8 h-1 rounded-full bg-surface-alt overflow-hidden shrink-0">
        <span className="block h-full rounded-full"
              style={{ width: `${value}%`, background: colour }} />
      </span>
    </span>
  );
}

export function ClientList() {
  const navigate = useNavigate();
  const [segment, setSegment] = useState("");
  const [lifecycle, setLifecycle] = useState("");
  const [sort, setSort] = useState("-revenue");
  // Server-side: the list is capped, so a box that only filters what came back
  // reports "no such client" for anyone outside the cap.
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["client-list", segment, lifecycle, sort, search],
    queryFn: async () => {
      const { data } = await api.get<AnalyticsEnvelope<Summary>>("/clients", {
        params: { segment, lifecycle, sort, search: search || undefined },
      });
      return data;
    },
    staleTime: 300_000,
  });

  const rows = (data?.breakdown ?? []) as unknown as ClientRow[];
  const summary = data?.summary;
  const truncated = Boolean(summary?.clients && rows.length < summary.clients);

  const columns = useMemo<Column<ClientRow>[]>(() => [
    { key: "name", header: "Client", width: "220px" },
    { key: "referral_type", header: "Type", width: "110px" },
    { key: "area", header: "Area", width: "140px" },
    { key: "owner", header: "Owner", width: "160px" },
    {
      key: "revenue", header: "Revenue", align: "right",
      render: (r) => inr(r.revenue), value: (r) => r.revenue,
    },
    {
      key: "commission", header: "Commission", align: "right", hidden: true,
      render: (r) => inr(r.commission), value: (r) => r.commission,
    },
    { key: "admissions", header: "Admissions", align: "right", value: (r) => r.admissions },
    {
      key: "conversion_pct", header: "Conv.", align: "right",
      render: (r) => pct(r.conversion_pct), value: (r) => r.conversion_pct,
    },
    { key: "activities", header: "Activities", align: "right", value: (r) => r.activities },
    {
      key: "days_since_activity", header: "Last seen", align: "right",
      render: (r) => (r.days_since_activity === null
        ? <span className="text-ink-faint">never</span>
        : <span className={r.days_since_activity > 30 ? "text-warn" : ""}>
            {r.days_since_activity}d
          </span>),
      value: (r) => r.days_since_activity,
    },
    {
      key: "health", header: "Health", align: "right",
      render: (r) => <ScoreCell value={r.health} />, value: (r) => r.health,
    },
    {
      key: "opportunity", header: "Opportunity", align: "right",
      render: (r) => <ScoreCell value={r.opportunity} />, value: (r) => r.opportunity,
    },
    {
      key: "risk", header: "Risk", align: "right",
      render: (r) => <ScoreCell value={r.risk} invert />, value: (r) => r.risk,
    },
    { key: "rfm_segment", header: "RFM", width: "130px" },
    { key: "lifecycle", header: "Lifecycle", width: "120px" },
  ], []);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display font-bold text-[24px] tracking-tight">Clients</h1>
        <p className="text-[13.5px] text-ink-muted mt-0.5 max-w-3xl">
          The client is the referrer — a doctor, RMP, technician or clinic who
          sends patients. Sort by <strong>Opportunity</strong> to find the
          valuable relationships that have gone quiet; that is a different list
          from Health or Risk, which is why all three are here.
        </p>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))" }}>
        <KpiCard loading={isLoading} label="Clients in scope" value={count(summary?.clients)} />
        <KpiCard loading={isLoading} label="Revenue attributed" value={inr(summary?.revenue)} />
        <KpiCard loading={isLoading} label="At risk (score ≥ 60)"
                 value={count(summary?.at_risk)} higherIsBetter={false} />
        <KpiCard loading={isLoading} label="Dormant or lost"
                 value={count(summary?.dormant)} higherIsBetter={false} />
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <label className="flex flex-col gap-1">
          <span className="label">RFM segment</span>
          <select value={segment} onChange={(e) => setSegment(e.target.value)}
                  className="text-sm bg-surface border border-rule rounded-md px-2 py-1.5">
            <option value="">All</option>
            {SEGMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="label">Lifecycle</span>
          <select value={lifecycle} onChange={(e) => setLifecycle(e.target.value)}
                  className="text-sm bg-surface border border-rule rounded-md px-2 py-1.5">
            <option value="">All</option>
            {LIFECYCLES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="label">Rank by</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)}
                  className="text-sm bg-surface border border-rule rounded-md px-2 py-1.5">
            <option value="-revenue">Revenue</option>
            <option value="-opportunity">Opportunity score</option>
            <option value="-risk">Risk score</option>
            <option value="health">Health (worst first)</option>
            <option value="-days_since_activity">Longest unseen</option>
            <option value="-admissions">Admissions</option>
          </select>
        </label>
      </div>

      {truncated && (
        <p className="flex items-start gap-2 text-[12.5px] text-ink-muted">
          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-warn" />
          <span>
            Showing {count(rows.length)} of {count(summary?.clients)} clients,
            ranked by {sort.replace("-", "")}. The rest are not missing —
            use the search box, which queries every client, not just these.
          </span>
        </p>
      )}

      <DataTable<ClientRow>
        rows={rows}
        columns={columns}
        rowKey={(r) => r.client_id}
        onRowClick={(r) => navigate(`/clients/${encodeURIComponent(r.client_id)}`)}
        exportName="clients"
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search every client by name, area or owner"
        loading={isLoading}
        emptyMessage="No clients match these filters. Scores are built by the nightly job after the first extract."
        footnote="Click a row for Client 360 — engagement, business, the three scores and the journey timeline."
      />

      <Disclosure quality={data?.quality} metadata={data?.metadata} />
    </div>
  );
}
